import { useApi } from '@/api';
import {
  ActivityCard,
  ExternalLinkIcon,
  KpiCard,
  Link,
  LinkButton,
  Page,
  PageSkeleton
} from '@/components';

import LockStakeIcon from '@/components/icons/LockStake';
import { tokensMapping } from '@/utils/tokens';
import { Flex, Heading, LinkWrapper } from '@blockanalitica/ui';
import CollateralAtRiskTabs from './CollateralAtRiskTabs';
import HistoricTabs from './HistoricTabs';
import SealKpis from './SealKpis';
import TopLocked from './TopLocked';
import TopVaults from './TopVaults';
import VaultsDonutChart from './VaultsDonutChart';
export default function SealILKPage() {
  const ilk = 'LSE-MKR-A';
  const [data, error] = useApi({
    url: `/groups/seal/ilks/${ilk}/`,
    queryParams: {
      days_ago: 1
    }
  });

  const [lseData, lseError] = useApi({
    url: `/lse/`,
    queryParams: {
      days_ago: 1
    }
  });

  // const restrictedKpi = (
  //   <>
  //     {
  //       // @ts-ignore
  //       import.meta.env.VITE_PUBLIC_RESTRICTED_BUILD !== 'true' ? (
  //         <>
  //           <Heading size="3">Seal Rewards</Heading>
  //           <LSEFarmsKpis daysAgo={'1'} />
  //         </>
  //       ) : null
  //     }
  //   </>
  // );

  const restrictedAction = (
    <>
      {
        // @ts-ignore
        import.meta.env.VITE_PUBLIC_RESTRICTED_BUILD !== 'true' ? (
          <LinkButton to={`https://app.sky.money/?widget=seal`} target="_blank">
            <Flex
              gap="3xs"
              alignItems="center"
              style={{ whiteSpace: 'nowrap' }}>
              Seal MKR tokens
              <ExternalLinkIcon size="1" />
            </Flex>
          </LinkButton>
        ) : null
      }
    </>
  );

  if (error || lseError) {
    throw error;
  }

  if (data && lseData) {
    return (
      <Page
        title={
          <Flex gap="3xs" alignItems="center">
            <LockStakeIcon size="3" />
            <Heading size="1">Seal Engine</Heading>
          </Flex>
        }
        actions={restrictedAction}>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            variant="mkr"
            title="Total MKR Locked"
            data={lseData}
            field="total_mkr"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  currency: {
                    name: data.collateral_symbol,
                    address: data.collateral_address
                  },
                  iconSize: '3'
                }
              },
              variants: { kpiCardFooter: 'muted' }
            }}
          />
          <KpiCard
            title="Total Borrowed"
            variant="dai"
            data={data}
            field="debt"
            options={{
              trend: true,
              valueOptions: {
                currencyValueOptions: {
                  currency: tokensMapping('USDS'),
                  iconSize: '3'
                }
              }
            }}
          />
        </Flex>

        <HistoricTabs
          ilk={ilk}
          collateralSymbol={data.collateral_symbol}
          collateralAddress={data.collateral_address}
        />

        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Weighted Collateral Ratio"
            data={data}
            field="weighted_cr"
            options={{
              percentage: true,
              trend: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
        </Flex>
        <SealKpis />

        {/* {restrictedKpi} */}

        <Flex gap={['m', 's']} flexDirection="column">
          <Flex gap="s" justifyContent="space-between" alignItems="center">
            <Heading size="3">Parameters</Heading>
            <LinkButton
              size="small"
              to="parameters"
              variant="secondary"
              height="1.2rem">
              view all
            </LinkButton>
          </Flex>
          <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
            <LinkWrapper
              to="parameters/stability-fee"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="Stability Fee"
                data={data}
                field="duty"
                options={{
                  percentage: true,
                  trend: true,
                  variants: { valuePercentageValue: 'dynamic' },
                  linkArrow: true
                }}
              />
            </LinkWrapper>
            <KpiCard
              title="Liquidation Ratio"
              data={data}
              field="mat"
              options={{
                percentage: true,
                trend: true,
                variants: { valuePercentageValue: 'dynamic' }
              }}
            />
            <KpiCard
              title="Debt Ceiling"
              data={data}
              field="line"
              options={{
                currency: tokensMapping('USDS'),
                notation: 'compact',
                trend: true,
                variants: { valuePercentageValue: 'dynamic' }
              }}
            />
            <KpiCard
              title="Dust"
              data={data}
              field="dust"
              options={{
                currency: tokensMapping('USDS'),
                notation: 'compact',
                trend: true,
                variants: { valuePercentageValue: 'dynamic' }
              }}
            />
          </Flex>
          <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
            <KpiCard
              title="OSM Price"
              data={data}
              field="osm_price"
              options={{
                currency: 'USD'
              }}
            />
            <KpiCard
              title="Next OSM Price"
              data={data}
              field="next_price"
              options={{
                currency: 'USD'
              }}
            />
          </Flex>
        </Flex>

        <ActivityCard
          url={`/groups/core/ilks/${ilk}/activity/`}
          linkTo="activity"
          title="Activity"
        />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="cdps?what=wallets"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Wallets"
                data={lseData}
                field="number_of_urns"
                options={{
                  trend: true,
                  variants: { valuePercentageValue: 'dynamic' },
                  linkArrow: true
                }}
              />
            </LinkWrapper>
            <TopLocked ilk={ilk} />
          </Flex>

          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="4">Wallets Distribution</Heading>
            <VaultsDonutChart ilk={ilk} type={'collateral'} />
          </Flex>
        </Flex>
        {Number(data.num_urns) > 0 && (
          <>
            <CollateralAtRiskTabs ilk={ilk} />
            <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
              <Flex gap={['m', 's']} flexDirection="column" width="100%">
                <LinkWrapper
                  to="cdps?what=borrowers"
                  // @ts-ignore
                  as={Link}
                  variant="kpiCardLinkWithBlendMode">
                  <KpiCard
                    title="N° of Borrowers"
                    data={data}
                    field="num_urns"
                    options={{
                      trend: true,
                      variants: { valuePercentageValue: 'dynamic' },
                      linkArrow: true
                    }}
                  />
                </LinkWrapper>
                <TopVaults ilk={ilk} />
              </Flex>

              <Flex flexBasis="100%" flexDirection="column" alignItems="center">
                <Heading size="4">Borrowers Distribution</Heading>
                <VaultsDonutChart ilk={ilk} type={'debt'} />
              </Flex>
            </Flex>
          </>
        )}
      </Page>
    );
  }

  return <PageSkeleton />;
}
